var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect } from "react";
import { Box, Tab, Tabs, useMediaQuery, useTheme, Skeleton, Stack, } from "@mui/material";
import { useParams } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
// Components
import { PageContainer, PageContent } from "../../../components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
// Hooks
import { useAPData, useAPForm } from "./hooks";
// Components
import { GeneralSettings, AlarmSettings, ServerSettings } from "./components";
import { canAccessApServerSettingsTab } from "@utility/permission-checker";
// Tab Panel Component
const TabPanel = (_a) => {
    var { children, value, index } = _a, other = __rest(_a, ["children", "value", "index"]);
    // Keep children mounted once the tab has been visited so unsaved form state
    // (e.g. a new contact row in Alarm Settings) survives tab switches.
    const [hasBeenActive, setHasBeenActive] = React.useState(value === index);
    React.useEffect(() => {
        if (value === index)
            setHasBeenActive(true);
    }, [value, index]);
    return (React.createElement("div", Object.assign({ role: "tabpanel", hidden: value !== index, id: `ap-tabpanel-${index}`, "aria-labelledby": `ap-tab-${index}` }, other), hasBeenActive && React.createElement(Box, { sx: { py: 1 } }, children)));
};
const EditAPPage = () => {
    const { t } = useTranslation(["form", "validation", "common"]);
    const { apId } = useParams();
    const [currentTab, setCurrentTab] = useState(0);
    const [alarmRecipientSaveBlocked, setAlarmRecipientSaveBlocked] = useState(false);
    const alarmSettingsRef = useRef(null);
    const serverSettingsRef = useRef(null);
    // Use custom hooks for data and form management
    const { isEdit, ap, apExists, loading } = useAPData(apId);
    const { methods, control, errors, handleSubmit, validationRules, handleFormSubmit, isSubmitting, } = useAPForm({ isEdit, ap });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const showServerSettingsTab = canAccessApServerSettingsTab();
    useEffect(() => {
        if (!showServerSettingsTab && currentTab >= 2) {
            setCurrentTab(1);
        }
    }, [showServerSettingsTab, currentTab]);
    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };
    // Handle save based on current tab
    const handleSave = () => {
        var _a, _b;
        if (currentTab === 0) {
            (_a = alarmSettingsRef.current) === null || _a === void 0 ? void 0 : _a.submitForm();
        }
        else if (currentTab === 1) {
            handleSubmit(handleFormSubmit)();
        }
        else if (showServerSettingsTab && currentTab === 2) {
            (_b = serverSettingsRef.current) === null || _b === void 0 ? void 0 : _b.submitForm();
        }
    };
    // Show loading skeleton while fetching AP
    if (isEdit && loading) {
        return (React.createElement(PageContainer, null,
            React.createElement(Box, { sx: { p: 3 } },
                React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 2, sx: { mb: 3 } },
                    React.createElement(Skeleton, { variant: "circular", width: 40, height: 40 }),
                    React.createElement(Skeleton, { variant: "rectangular", width: 200, height: 32, sx: { borderRadius: 1 } }))),
            React.createElement(PageContent, null,
                React.createElement(Stack, { spacing: 3 },
                    React.createElement(Skeleton, { variant: "rectangular", height: 48, sx: { borderRadius: 1 } }),
                    React.createElement(Skeleton, { variant: "rectangular", height: 90, sx: { borderRadius: 1 } }),
                    React.createElement(Box, null,
                        React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 1.5 } },
                            React.createElement(Skeleton, { variant: "text", width: 80, height: 32 }),
                            React.createElement(Skeleton, { variant: "circular", width: 20, height: 20 })),
                        React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 } },
                            React.createElement(Skeleton, { variant: "rectangular", width: 42, height: 24, sx: { borderRadius: 12 } }),
                            React.createElement(Skeleton, { variant: "text", width: 260, height: 20 })),
                        React.createElement(Stack, { direction: "row", spacing: 2, sx: { mb: 2 } },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } })),
                        React.createElement(Stack, { direction: "row", spacing: 2 },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }))),
                    React.createElement(Box, { sx: { bgcolor: "background.paper", borderRadius: 4, px: 2, py: 2 } },
                        React.createElement(Skeleton, { variant: "text", width: 130, height: 32, sx: { mb: 2 } }),
                        React.createElement(Stack, { direction: "row", spacing: 2, alignItems: "center", sx: { mb: 1.5 } },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "circular", width: 36, height: 36 })),
                        React.createElement(Stack, { direction: "row", spacing: 2, alignItems: "center", sx: { mb: 1.5 } },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 2.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1.5, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "circular", width: 36, height: 36 })),
                        React.createElement(Stack, { direction: "row", justifyContent: "flex-end", sx: { mt: 1 } },
                            React.createElement(Skeleton, { variant: "rectangular", width: 140, height: 36, sx: { borderRadius: 1 } })))))));
    }
    // Early return for AP not found
    if (isEdit && !apExists) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.accessPointNotFound"), showBackButton: true })));
    }
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader
        // title={isEdit ? t("common:pages.editAp") : t("common:pages.addAp")}
        , { 
            // title={isEdit ? t("common:pages.editAp") : t("common:pages.addAp")}
            title: "", showBackButton: true, bottomActionButton: {
                label: t("common:actions.saveChanges"),
                onClick: handleSave,
                variant: "contained",
                size: "large",
                disabled: isSubmitting ||
                    (currentTab === 0 && alarmRecipientSaveBlocked),
            } }),
        React.createElement(PageContent, { sx: { pb: 0, pr: 0 } },
            React.createElement(FormProvider, Object.assign({}, methods),
                React.createElement(Box, { sx: { width: "100%" } },
                    React.createElement(Box, null,
                        React.createElement(Tabs, { value: currentTab, onChange: handleTabChange },
                            React.createElement(Tab, { label: t("common:tabs.alarmSettings"), id: "ap-tab-0" }),
                            React.createElement(Tab, { label: t("common:tabs.generalSettings"), id: "ap-tab-1" }),
                            showServerSettingsTab && (React.createElement(Tab, { label: t("common:tabs.serverSettings"), id: "ap-tab-2" })))),
                    React.createElement(Box, { sx: {
                            maxHeight: `calc(100vh - ${isMobile ? 205 : 215}px)`,
                            minHeight: `calc(100vh - ${isMobile ? 205 : 215}px)`,
                            overflowY: "auto",
                            pb: 3,
                            pr: { xs: 1.5, sm: 2, lg: 3 },
                        } },
                        React.createElement(TabPanel, { value: currentTab, index: 0 },
                            React.createElement(AlarmSettings, { ref: alarmSettingsRef, control: control, errors: errors, validationRules: validationRules, isEdit: isEdit, ap: ap, onRecipientSaveBlockedChange: setAlarmRecipientSaveBlocked })),
                        React.createElement(TabPanel, { value: currentTab, index: 1 },
                            React.createElement(GeneralSettings, { control: control, errors: errors, validationRules: validationRules, isEdit: isEdit, ap: ap })),
                        showServerSettingsTab && (React.createElement(TabPanel, { value: currentTab, index: 2 },
                            React.createElement(ServerSettings, { ref: serverSettingsRef, control: control, errors: errors, validationRules: validationRules, isEdit: isEdit, ap: ap })))))))));
};
export default EditAPPage;
